import Button from "@/components/Button";
import CopyButton from "@/components/CopyButton";
import MenuButton from "./MenuButton";
import { consolidateActions, OVERFLOW_MENU_LABEL, type Action } from "./actionConsolidation";

export interface ActionClusterProps {
  actions?: Action[];
  /** Override the MenuButton trigger testId. Defaults to {@link DEFAULT_MENU_BUTTON_TEST_ID}. */
  menuButtonTestId?: string;
  /**
   * Override the overflow menu trigger label. Defaults to {@link OVERFLOW_MENU_LABEL}
   * (`More`). Focused-page compact collapse passes `Actions` from TopAppBar.
   */
  menuButtonLabel?: string;
  /**
   * Max eligible actions to keep inline when there are fewer than three total
   * actions. Defaults to 2. A pair is never split into a one-item menu.
   */
  maxInline?: number;
}

/** Renders 1–2 actions inline when the cluster has fewer than three total actions; three or more (including `menuOnly`) overflow after the first eligible. */
export default function ActionCluster({ actions, menuButtonTestId, menuButtonLabel, maxInline }: ActionClusterProps) {
  const {
    inlineActions,
    menuItems,
    menuButtonTestId: resolvedMenuButtonTestId
  } = consolidateActions({
    actions,
    menuButtonTestId,
    maxInline
  });
  const resolvedMenuButtonLabel = menuButtonLabel ?? OVERFLOW_MENU_LABEL;

  if (inlineActions.length === 0 && menuItems.length === 0) {
    return null;
  }

  return (
    <>
      {inlineActions.map((action, index) =>
        action.copyButton ? (
          <CopyButton
            key={index}
            textToCopy={action.copyButton.textToCopy}
            ariaLabel={action.copyButton.ariaLabel ?? action.ariaLabel}
            copyFieldLabel={action.copyButton.copyFieldLabel}
            copyFieldId={action.copyButton.copyFieldId}
            feedback={action.copyButton.feedback}
            successMessage={action.copyButton.successMessage}
            onCopied={action.copyButton.onCopied}
            variant={action.variant}
            testId={action.testId}
          >
            {action.text}
          </CopyButton>
        ) : (
          <Button
            key={index}
            testId={action.testId || undefined}
            ariaLabel={action.ariaLabel}
            variant={action.variant || "filled"}
            theme={action.theme}
            onClick={action.onClick}
            icon={action.iconName}
            iconClassName={action.iconClassName}
            disabled={action.disabled}
          >
            {action.text}
          </Button>
        )
      )}
      {menuItems.length > 0 && (
        <MenuButton actions={menuItems} testId={resolvedMenuButtonTestId} label={resolvedMenuButtonLabel} />
      )}
    </>
  );
}
